/**
 * ================================================================
 * REDUX ACTIONS: Global Store Reset
 * ================================================================
 *
 * @fileOverview     Defines global reset action for clearing entire Redux store state
 * @module           store/actions/resetActions
 * @version          1.0.0
 * @since            2025-08-13
 * @author           Development Team <dev@company.com>
 * @lastModified     2025-08-13
 *
 * @purpose          Provides a single action (RESET_ALL) that when dispatched triggers
 *                   the meta-reducer to reinitialize all slices to their initial state.
 * @dependencies     Redux Toolkit action creators (lightweight) - no slice coupling
 * @exports          RESET_ALL constant, resetAll action creator
 *
 * @architecture
 * - Action constant + FSA compliant action creator
 * - Consumed by store meta-reducer in store/index.ts
 * - Avoids circular dependencies by isolating in actions directory
 *
 * @performance      Negligible overhead; action only used on logout/session invalidation
 * @security         Ensures sensitive session data purged from memory on logout
 * @accessibility    Not applicable (state management only)
 * @testing          Dispatch resetAll and assert state equals initial state for slices
 *
 * @relatedFiles
 * - /src/store/index.ts - Meta-reducer implementation consuming RESET_ALL
 * - /src/components/layout/Header.tsx - Dispatch site-wide on user logout
 *
 * ================================================================
 */

// ================================================================
// ACTION TYPE CONSTANT
// ================================================================
/** Global store reset action type */
export const RESET_ALL = 'RESET_ALL' as const;

// ================================================================
// ACTION CREATOR
// ================================================================
/**
 * Create a FSA-compliant action to reset entire Redux store
 *
 * @returns {{ type: typeof RESET_ALL }} Reset action object
 */
export const resetAll = () => ({ type: RESET_ALL });

/**
 * ================================================================
 * END OF FILE: resetActions.ts
 * ================================================================
 *
 * @summary          Action type & creator for global store reset
 * @exports          RESET_ALL, resetAll
 * @dependencies     None (standalone action creator)
 * @usedBy           Store meta-reducer, logout flows
 *
 * @changelog
 * - 2025-08-13: Initial implementation
 *
 * @todos
 * - [ ] Add unit test validating state reset behavior
 *
 * ================================================================
 */
